import { unauthenticatedError } from '@minty/shared/auth';
import { TRPCClientError } from '@trpc/client';

/**
 * Reading a refusal back out of a failed call.
 *
 * The server throws `AppError(code, key)`; the key arrives as
 * `error.data.appCode`, and anything it attached to explain the refusal (the
 * name of whoever owns a channel, say) as `error.data.cause`. This is where
 * that shape is unwrapped once, instead of casts at every call site.
 */
type AppErrorData = {
  appCode?: unknown;
  cause?: unknown;
  code?: unknown;
  httpStatus?: unknown;
};

function errorData(error: unknown): AppErrorData | null {
  if (!(error instanceof TRPCClientError)) {
    return null;
  }

  const data: unknown = error.data;

  return typeof data === 'object' && data !== null ? data : null;
}

/** The refusal's key, or `null` when this was not an application error. */
export function appErrorCode(error: unknown): string | null {
  const code = errorData(error)?.appCode;

  return typeof code === 'string' ? code : null;
}

/** One named detail the server attached to the refusal, as text. */
export function appErrorDetail(error: unknown, name: string): string | null {
  const cause = errorData(error)?.cause;

  if (typeof cause !== 'object' || cause === null) {
    return null;
  }

  const value = (cause as Record<string, unknown>)[name];

  return typeof value === 'string' ? value : null;
}

/** Whoever the server named in the refusal — the current owner of a channel
 *  somebody is about to take. */
export function appErrorOwnerName(error: unknown): string | null {
  return appErrorDetail(error, 'ownerName');
}

/** The value the refusal is about, echoed back exactly as it was sent — which
 *  is how a form with two channel boxes knows which one to mark. */
export function appErrorValue(error: unknown): string | null {
  return appErrorDetail(error, 'value');
}

/** A refusal the person can do nothing about by retrying. */
export function isClientError(error: unknown): boolean {
  const status = errorData(error)?.httpStatus;

  return typeof status === 'number' && status < 500;
}

/** The call failed because nobody is signed in (any more). */
export function isAuthError(error: unknown): boolean {
  const data = errorData(error);

  return (
    data?.appCode === unauthenticatedError || data?.code === 'UNAUTHORIZED'
  );
}
